import type { FeatureType } from "schemas/agent-billing";

import * as React from "react";

import Editor from "@monaco-editor/react";
import { SelectionItem } from "@replohq/lattice/blocks/selection/SelectionItem";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { useMockCustomPricingEnabled } from "@v2/features/agent-billing/hooks/mockCustomPricing";
import { agentBillingRouter } from "@v2/trpc/routes/agentBilling";
import { format } from "date-fns";
import { ExternalLink } from "lucide-react";

export const ALWAYS_FRESH = { refetchOnMount: "always" as const, staleTime: 0 };

export function amountColorClass(amount: number): string {
  if (amount > 0) {
    return "text-emerald";
  }
  if (amount < 0) {
    return "text-rose";
  }

  return "text-default";
}

export function eventTypeTagVariant(
  eventType: string,
): "success" | "danger" | "info" {
  if (eventType === "created") {
    return "success";
  }
  if (eventType === "canceled") {
    return "danger";
  }

  return "info";
}

export function formatDate(date: Date | string | null | undefined): string {
  if (!date) {
    return "--";
  }

  return format(new Date(date), "MMM d, yyyy HH:mm:ss");
}

export function getStripeDashboardUrl(id: string): string | null {
  const prefixMap: Record<string, string> = {
    sub_: "subscriptions",
    cus_: "customers",
    evt_: "events",
    pi_: "payment_intents",
    ch_: "payments",
    in_: "invoices",
    ii_: "invoice_items",
    price_: "prices",
    prod_: "products",
  };

  for (const [prefix, resource] of Object.entries(prefixMap)) {
    if (id.startsWith(prefix)) {
      return `https://dashboard.stripe.com/${resource}/${id}`;
    }
  }

  return null;
}

export const StripeLink: React.FC<{ value: string | null | undefined }> = ({
  value,
}) => {
  if (!value) {
    return <span className="text-muted">--</span>;
  }

  const url = getStripeDashboardUrl(value);
  if (!url) {
    return <span className="font-mono">{value}</span>;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 font-mono text-primary hover:underline"
    >
      {value}
      <ExternalLink className="size-3 shrink-0" />
    </a>
  );
};

export const CopyableId: React.FC<{ value: string | null | undefined }> = ({
  value,
}) => {
  const [copied, setCopied] = React.useState(false);

  if (!value) {
    return <span className="text-muted">--</span>;
  }

  return (
    <button
      type="button"
      className="cursor-pointer font-mono text-xs text-primary hover:underline"
      onClick={() => {
        void navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? "Copied!" : value}
    </button>
  );
};

export const KeyValue: React.FC<
  React.PropsWithChildren<{
    label: string;
  }>
> = ({ label, children }) => {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="text-muted shrink-0 text-xs">{label}</span>
      <span className="text-default text-right text-xs">{children}</span>
    </div>
  );
};

export const DebugSection: React.FC<
  React.PropsWithChildren<{
    title: string;
  }>
> = ({ title, children }) => {
  return (
    <div className="flex flex-col gap-2 px-3 py-3">
      <span className="text-default text-xs font-semibold">{title}</span>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
};

export const MockCustomPricingToggle: React.FC = () => {
  const [isMockCustomPricingEnabled, setIsMockCustomPricingEnabled] =
    useMockCustomPricingEnabled();

  return (
    <SelectionItem
      id="debug-mock-custom-pricing"
      type="switch"
      variant="outline"
      label="Mock custom pricing"
      description="Renders the billing page as a custom-contract workspace."
      checked={isMockCustomPricingEnabled ?? false}
      onCheckedChange={() => {
        setIsMockCustomPricingEnabled(!isMockCustomPricingEnabled);
      }}
      UNSAFE_className="p-2 [&_[data-slot=field-label]]:text-xs [&_[data-slot=field-description]]:text-xs"
    />
  );
};

const READONLY_JSON_OPTIONS = {
  readOnly: true,
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
  lineNumbers: "off" as const,
  fontSize: 12,
  renderLineHighlight: "none" as const,
  automaticLayout: true,
};

export const ReadOnlyJsonEditor: React.FC<{ value: unknown }> = ({ value }) => {
  const formatted = JSON.stringify(value, null, 2);
  const lineCount = formatted.split("\n").length;
  const height = Math.min(lineCount * 18 + 16, 400);

  return (
    <div className="border-border overflow-hidden rounded-lg border">
      <Editor
        height={height}
        defaultLanguage="json"
        value={formatted}
        options={READONLY_JSON_OPTIONS}
        theme="vs"
      />
    </div>
  );
};

export function useCapacityUsage() {
  const workspaceId = useCurrentWorkspaceId();
  const queryInput = (
    featureType:
      | "sites"
      | "shopifyThemeBuilder.projects"
      | "shopifyThemeBuilder.publishedElements",
  ) => {
    return workspaceId ? { workspaceId, featureType } : skipToken;
  };

  const { data: sites } = agentBillingRouter.capacity.check.useQuery(
    queryInput("sites"),
    ALWAYS_FRESH,
  );
  const { data: projects } = agentBillingRouter.capacity.check.useQuery(
    queryInput("shopifyThemeBuilder.projects"),
    ALWAYS_FRESH,
  );
  const { data: publishedElements } =
    agentBillingRouter.capacity.check.useQuery(
      queryInput("shopifyThemeBuilder.publishedElements"),
      ALWAYS_FRESH,
    );

  return React.useMemo(() => {
    const map: Partial<
      Record<FeatureType, { current: number; maximum: number | null }>
    > = {};

    if (sites) {
      map.sites = sites;
    }
    if (projects) {
      map["shopifyThemeBuilder.projects"] = projects;
    }
    if (publishedElements) {
      map["shopifyThemeBuilder.publishedElements"] = publishedElements;
    }

    return map;
  }, [sites, projects, publishedElements]);
}
